import { useState, useCallback } from 'react'
import { billingService } from '../services/BillingService'
import { useAuth } from './useAuth'
import { STRIPE_PRICES } from '../config/stripe-prices'
import type { CreateCheckoutRequest } from '@/types/billing'

export const useCheckout = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  const initiateCheckout = useCallback(async (
    planId: string,
    billingCycle: 'monthly' | 'annual'
  ) => {
    if (planId === 'free') {
      // For free trial, just redirect to signup
      return { success: true, requiresSignup: true }
    }

    setLoading(true)
    setError(null)

    try {
      // Get the correct price ID from the config
      const planConfig = STRIPE_PRICES[planId as keyof typeof STRIPE_PRICES]
      if (!planConfig) {
        throw new Error(`Unknown plan: ${planId}`)
      }

      const priceId = billingCycle === 'annual' ? planConfig.annual : planConfig.monthly

      // Different success URLs for authenticated vs guest users
      // Authenticated users (upgrading) → Show PaymentSuccess modal
      // Guest users (paying before signup) → Show CreateAccountAfterPayment flow
      const successUrl = user
        ? `${window.location.origin}/?success=true&session_id={CHECKOUT_SESSION_ID}`
        : `${window.location.origin}/create-account?session_id={CHECKOUT_SESSION_ID}`

      const request: CreateCheckoutRequest = {
        price_id: priceId,
        billing_cycle: billingCycle === 'annual' ? 'annual' : 'monthly',
        success_url: successUrl,
        cancel_url: window.location.href
      }

      console.log('🔍 useCheckout - Creating checkout session')
      console.log('🔍 useCheckout - User authenticated:', !!user)
      console.log('🔍 useCheckout - Success URL:', request.success_url)

      const session = await billingService.createCheckoutSession(request)
      window.location.href = session.checkout_url

      return { success: true, requiresSignup: false }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate checkout'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  return {
    initiateCheckout,
    loading,
    error,
    clearError: () => setError(null)
  }
}